import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import { CardMedia, Link as MUILink, Tooltip, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';

import { AppPath } from '@/shared/constants/path';
import FulfilmentHistory from '@/shared/types/fulfilment/history.type';
import OrderProduct from '@/shared/types/order/orderProduct.type';
import formatCurrency from '@/shared/utils/formatCurrency';

import HistoryItem from './HistoryItem';
import StatusIndicator from './HistoryItem/components/StatusIndicator';

interface ProductDetailsProps {
  product: OrderProduct;
  historyItem?: FulfilmentHistory;
  getProductItemRect?: (rect: DOMRect) => void;
}

const ProductDetails: FC<ProductDetailsProps> = ({
  product,
  historyItem,
  getProductItemRect,
}) => {
  const { t } = useTranslation(['orders', 'common']);
  const productDetailsContainerRef = useRef<HTMLDivElement | null>(null);

  const productImage = '';

  return (
    <Stack
      direction='row'
      key={product.product_id}
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      ref={productDetailsContainerRef}
    >
      <Stack direction='row' sx={{ gap: 2, alignItems: 'center' }}>
        <CardMedia
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 107,
            height: 80,
            borderRadius: 1,
            backgroundColor: 'grey.300',
          }}
          image={productImage}
          title='green iguana'
        >
          {!productImage && (
            <InsertPhotoOutlinedIcon
              sx={{ width: 24, height: 24, color: 'white' }}
            />
          )}
        </CardMedia>

        <Box>
          <MUILink
            component={Link}
            underline='none'
            variant='body1'
            to={AppPath.Products + `/${product.product_id}`}
          >
            {product.product_name}
          </MUILink>

          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              variant='body2'
              sx={{
                'color': 'text.secondary',
                'cursor': 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              onClick={() => {
                if (getProductItemRect && productDetailsContainerRef.current) {
                  getProductItemRect(
                    productDetailsContainerRef.current.getBoundingClientRect(),
                  );
                }
              }}
            >
              {t('orders:fulfilmentDetails')}
            </Typography>

            {historyItem && (
              <Tooltip
                title={<HistoryItem historyItem={historyItem} />}
                placement='right-start'
                tabIndex={-1}
                arrow
                componentsProps={{
                  tooltip: { sx: { maxWidth: 500 } },
                }}
              >
                <span>
                  <StatusIndicator status={historyItem.status} />
                </span>
              </Tooltip>
            )}
          </Stack>
        </Box>
      </Stack>

      <Stack direction='row' spacing={2}>
        <Typography variant='body2' color='text.secondary'>
          {formatCurrency(
            Number(product.pre_tax_price ?? '') / Number(product.product_qty),
          )}
        </Typography>

        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ textWrap: 'nowrap' }}
        >
          x {product.product_qty}
        </Typography>

        <Typography variant='body2'>
          {formatCurrency(
            (Number(product.pre_tax_price ?? '') /
              Number(product.product_qty)) *
              Number(product.product_qty),
          )}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default ProductDetails;
